import React, { forwardRef, useEffect, useState } from "react";

import { formatDateInCompanyTimeZone } from "utils/format";

const getTextValue = (...values) => {
  const match = values.find((value) => String(value ?? "").trim());
  return String(match ?? "").trim();
};

const getUserName = (user) => (
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim()
  || user?.name
  || user?.email
  || ""
);

const formatDateTime = (value, companyOrCode) => (
  formatDateInCompanyTimeZone(value, companyOrCode, "MMM dd, yyyy, h:mm a", "")
);

const getCertificateGrade = (certificate) => getTextValue(
  certificate?.templateData?.grade,
  certificate?.grade,
  certificate?.dynamicAttributes?.grade
);

const getVerificationDisplayUrl = (verificationUrl) => String(verificationUrl || "").replace(/^https?:\/\//i, "");

// logoImageContent.storedLink is a bare S3 object key (e.g. "44o676132USF/logo.png"), not a
// loadable URL — it needs an authenticated fetch-and-convert step (see the PDF/PNG export
// path) before it can be used as an <img src>. Using it directly here renders a broken-image
// icon, so only genuinely fetchable URLs are considered for the live preview.
const getCompanyLogoUrl = (companyDetails = {}, certificate = {}) => getTextValue(
  companyDetails?.logoImageContent?.displayLink,
  certificate?.company?.logoImageContent?.displayLink,
  companyDetails?.logoLocation,
  certificate?.company?.logoLocation,
  companyDetails?.logoUrl,
  certificate?.company?.logoUrl,
  companyDetails?.logo,
  certificate?.company?.logo,
  typeof window !== "undefined" ? window.localStorage.getItem("companyLogo") : ""
);

const getSignatureUrl = (certificate = {}) => getTextValue(
  certificate?.signature?.displayLink,
  certificate?.signature?.storedLink,
  certificate?.templateData?.signatures?.[0]?.displayLink,
  certificate?.templateData?.signatures?.[0]?.storedLink
);

const stripImageBackground = (imageUrl) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const context = canvas.getContext("2d");
      context.drawImage(img, 0, 0);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const { data } = imageData;
      for (let i = 0; i < data.length; i += 4) {
        const brightness = Math.min(data[i], data[i + 1], data[i + 2]);
        if (brightness > 250) {
          data[i + 3] = 0;
        } else if (brightness > 195) {
          data[i + 3] = Math.round(data[i + 3] * ((250 - brightness) / (250 - 195)));
        }
      }
      context.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    } catch {
      resolve(imageUrl);
    }
  };
  img.onerror = () => resolve(imageUrl);
  img.src = imageUrl;
});

const useSignatureImage = (signatureUrl) => {
  const [resolvedUrl, setResolvedUrl] = useState(signatureUrl);

  useEffect(() => {
    let cancelled = false;
    setResolvedUrl(signatureUrl);
    if (!signatureUrl) return undefined;

    stripImageBackground(signatureUrl).then((processedUrl) => {
      if (!cancelled) setResolvedUrl(processedUrl);
    });

    return () => {
      cancelled = true;
    };
  }, [signatureUrl]);

  return resolvedUrl;
};

const CertificateDocument = forwardRef(({ certificate, companyDetails, verificationUrl }, ref) => {
  const templateData = certificate?.templateData || {};
  const completionDate = formatDateTime(certificate?.completionDate, certificate?.company || companyDetails);
  const organizationName = getTextValue(companyDetails?.name, certificate?.company?.name, "AVPS Academy Inc").toUpperCase();
  const logoUrl = getCompanyLogoUrl(companyDetails, certificate);
  const holderName = getTextValue(certificate?.holderName, getUserName(certificate?.user));
  const courseName = getTextValue(certificate?.product?.name, certificate?.product?.title, certificate?.product?.displayName);
  const certificateId = getTextValue(certificate?.identifier);
  const grade = getCertificateGrade(certificate);
  const issuer = getTextValue(certificate?.issuedBy, companyDetails?.name, certificate?.company?.name);
  const signatureUrl = useSignatureImage(getSignatureUrl(certificate));
  const headerDescription = getTextValue(templateData.headerDescription);
  const accomplishmentText = getTextValue(templateData.accomplishmentText);
  const issuanceText = getTextValue(templateData.issuanceText);
  const description = getTextValue(certificate?.description);
  const footerDescription = getTextValue(templateData.footerDescription);
  const verificationDisplayUrl = getVerificationDisplayUrl(verificationUrl);
  const metaItems = [
    completionDate ? { label: "COMPLETED", value: completionDate } : null,
    certificateId ? { label: "CERTIFICATE ID", value: certificateId } : null,
    grade ? { label: "GRADE", value: grade } : null
  ].filter(Boolean);

  return (
    <div
      ref={ref}
      style={{
        width: "1123px",
        height: "794px",
        fontFamily: "Helvetica, Arial, sans-serif",
        backgroundColor: "#ffffff",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box"
      }}
    >
      <div style={{
        position: "absolute",
        inset: "24px",
        border: "1.5px solid #172033",
        borderRadius: "4px",
        pointerEvents: "none"
      }} />
      <div style={{
        position: "absolute",
        inset: "32px",
        border: "0.5px solid #c7a55b",
        borderRadius: "2px",
        pointerEvents: "none"
      }} />
      <div style={{
        position: "absolute",
        top: "24px",
        left: "24px",
        right: "24px",
        height: "4px",
        backgroundColor: "#172033"
      }} />

      <div style={{
        position: "absolute",
        top: "52px",
        left: "80px",
        right: "80px",
        bottom: "52px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center"
      }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt={`${organizationName} logo`}
            onError={(event) => {
              event.target.style.display = "none";
            }}
            style={{
              width: "auto",
              maxWidth: "52px",
              height: "auto",
              maxHeight: "52px",
              margin: "0 0 10px",
              display: "block"
            }}
          />
        )}
        <p style={{
          margin: "0",
          fontSize: "11px",
          fontWeight: "700",
          letterSpacing: "0.32em",
          color: "#172033",
          textTransform: "uppercase"
        }}>
          {organizationName}
        </p>

        <div style={{ width: "40px", height: "1px", backgroundColor: "#c7a55b", margin: "14px auto" }} />

        <p style={{
          margin: "0",
          fontSize: "10px",
          fontWeight: "700",
          letterSpacing: "0.3em",
          color: "#8a6b26",
          textTransform: "uppercase"
        }}>
          Certificate of Completion
        </p>

        <h1 style={{
          margin: "10px 0 0",
          fontSize: "52px",
          fontWeight: "700",
          letterSpacing: "0.1em",
          color: "#172033",
          textTransform: "uppercase",
          lineHeight: "1"
        }}>
          Certificate
        </h1>

        {headerDescription && (
          <p style={{
            margin: "12px 0 0",
            fontSize: "13px",
            color: "#64748b",
            lineHeight: "1.5",
            maxWidth: "600px"
          }}>
            {headerDescription}
          </p>
        )}

        {holderName && (
          <p style={{
            margin: "14px 0 0",
            fontSize: "36px",
            fontWeight: "700",
            color: "#172033",
            lineHeight: "1.1"
          }}>
            {holderName}
          </p>
        )}

        <div style={{ width: "480px", height: "1px", backgroundColor: "#c7a55b", margin: "12px auto 0" }} />

        {accomplishmentText && (
          <p style={{
            margin: "10px 0 0",
            fontSize: "13px",
            color: "#374151",
            lineHeight: "1.5",
            maxWidth: "680px"
          }}>
            {accomplishmentText}
          </p>
        )}

        {courseName && (
          <p style={{
            margin: "8px 0 0",
            fontSize: "18px",
            fontWeight: "700",
            color: "#1f3a5f"
          }}>
            {courseName}
          </p>
        )}

        {issuanceText && (
          <p style={{
            margin: "6px 0 0",
            fontSize: "12px",
            color: "#4b5563",
            lineHeight: "1.5",
            maxWidth: "680px"
          }}>
            {issuanceText}
          </p>
        )}

        {description && (
          <p style={{
            margin: "4px 0 0",
            fontSize: "11px",
            color: "#64748b",
            lineHeight: "1.5",
            maxWidth: "680px"
          }}>
            {description}
          </p>
        )}
      </div>

      {metaItems.length > 0 && (
        <div style={{
          position: "absolute",
          left: "80px",
          right: "80px",
          top: "512px",
          display: "grid",
          gridTemplateColumns: `repeat(${metaItems.length}, 1fr)`,
          border: "0.5px solid #e2d9be",
          borderRadius: "6px",
          overflow: "hidden",
          backgroundColor: "#fafaf8",
          boxShadow: "0 6px 18px rgba(23, 32, 51, 0.04)"
        }}>
          {metaItems.map((item, index) => (
            <div
              key={item.label}
              style={{
                padding: "12px 18px",
                textAlign: "center",
                borderLeft: index > 0 ? "0.5px solid #e2d9be" : "none"
              }}
            >
              <p style={{
                margin: "0",
                fontSize: "9px",
                fontWeight: "700",
                letterSpacing: "0.22em",
                color: "#8a6b26",
                textTransform: "uppercase"
              }}>
                {item.label}
              </p>
              <p style={{
                margin: "4px 0 0",
                fontSize: "13px",
                fontWeight: "700",
                color: "#172033",
                lineHeight: "1.3",
                overflowWrap: "anywhere",
                wordBreak: "break-word"
              }}>
                {item.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {issuer && (
        <div style={{
          position: "absolute",
          right: "100px",
          top: "640px",
          width: "220px",
          textAlign: "center"
        }}>
          {signatureUrl && (
            <img
              src={signatureUrl}
              alt="Signature"
              style={{ display: "block", margin: "0 auto 4px", maxHeight: "48px", maxWidth: "180px", objectFit: "contain" }}
            />
          )}
          <div style={{ height: "1px", backgroundColor: "#172033", marginBottom: "8px" }} />
          <p style={{
            margin: "0",
            fontSize: "11px",
            fontWeight: "700",
            color: "#172033"
          }}>
            {issuer}
          </p>
          <p style={{
            margin: "3px 0 0",
            fontSize: "9px",
            letterSpacing: "0.2em",
            color: "#8a6b26",
            textTransform: "uppercase"
          }}>
            Authorized Signatory
          </p>
        </div>
      )}

      {footerDescription && (
        <p style={{
          position: "absolute",
          top: "682px",
          left: "100px",
          maxWidth: "500px",
          margin: "0",
          fontSize: "10px",
          color: "#64748b",
          lineHeight: "1.5",
          textAlign: "left"
        }}>
          {footerDescription}
        </p>
      )}

      {verificationDisplayUrl && (
        <div style={{
          position: "absolute",
          bottom: "30px",
          left: "80px",
          right: "80px",
          borderTop: "0.5px solid #e2d9be",
          paddingTop: "6px",
          textAlign: "center"
        }}>
          <p style={{
            margin: "0 0 2px",
            fontSize: "8px",
            fontWeight: "700",
            color: "#8a6b26",
            letterSpacing: "0.12em",
            textTransform: "uppercase"
          }}>
            Verify Certificate
          </p>
          <a
            href={verificationUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              fontSize: "9px",
              fontWeight: "600",
              color: "#475569",
              textDecoration: "underline",
              letterSpacing: "0.01em",
              lineHeight: "1.35",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              maxWidth: "100%"
            }}
          >
            {verificationDisplayUrl}
          </a>
        </div>
      )}
    </div>
  );
});

CertificateDocument.displayName = "CertificateDocument";

export default CertificateDocument;
